/**
 * Events Service - Handles event management operations
 * Provides CRUD operations for events
 */

import { safeFrom, safeAuth, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notFoundError,
  notConfiguredError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';

export type Event = {
  id: number;
  title: string;
  date: string; // ISO date string (YYYY-MM-DD)
  time: string; // e.g. "2:00 PM - 6:00 PM"
  location: string;
  category: string;
  description: string;
  capacity: number;
  registered: number;
  featured: boolean;
  published: boolean;
  created_at: string;
  updated_at: string;
};

export type EventFormValues = {
  title: string;
  date: string; // ISO date string (YYYY-MM-DD)
  time: string;
  location: string;
  category: string;
  description: string;
  capacity: number;
  featured?: boolean;
  published?: boolean;
};

export const eventsService = {
  /**
   * Get all events (published only for public, all for admins)
   * @param isAdmin - Whether the request is from an admin user
   * @returns Array of events
   */
  async getEvents({ isAdmin = false }: { isAdmin?: boolean } = {}): Promise<ServiceResponse<Event[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }

    try {
      let query = safeFrom('events')
        .select('*')
        .order('date', { ascending: true });

      if (!isAdmin) {
        query = query.eq('published', true) as any;
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return successResponse((data || []) as Event[]);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'eventsService.getEvents'));
    }
  },

  /**
   * Get a single event by ID
   * @param id - Event ID
   * @param isAdmin - Whether the request is from an admin user
   * @returns Event or null
   */
  async getEvent(id: number, { isAdmin = false }: { isAdmin?: boolean } = {}): Promise<ServiceResponse<Event | null>> {
    if (!isSupabaseAvailable()) {
      return successResponse(null);
    }

    try {
      let query = safeFrom('events')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (!isAdmin) {
        query = query.eq('published', true) as any;
      }

      const { data, error } = await query;

      if (error) {
        if (error.code === 'PGRST116') {
          return successResponse(null);
        }
        throw error;
      }

      return successResponse(data as Event | null);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'eventsService.getEvent', { id }));
    }
  },

  /**
   * Create a new event
   * @param eventData - Event data
   * @returns Created event
   */
  async createEvent(eventData: EventFormValues): Promise<ServiceResponse<Event>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();
      if (user?.error) {
        throw user.error;
      }

      const payload: any = {
        title: eventData.title.trim(),
        date: eventData.date,
        time: eventData.time.trim(),
        location: eventData.location.trim(),
        category: eventData.category.trim(),
        description: eventData.description.trim(),
        capacity: Number(eventData.capacity) || 0,
        registered: 0,
        featured: eventData.featured ?? false,
        published: eventData.published ?? true,
      };

      const { data, error } = await safeFrom('events')
        .insert([payload])
        .select()
        .single();

      if (error) {
        throw error;
      }

      const event = data as Event;

      // Audit log
      await auditContent.create('event', event.id.toString(), {
        title: event.title,
        date: event.date,
        published: event.published,
      });

      return successResponse(event);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'eventsService.createEvent'));
    }
  },

  /**
   * Update an existing event
   * @param id - Event ID
   * @param eventData - Updated event data
   * @returns Updated event
   */
  async updateEvent(id: number, eventData: Partial<EventFormValues>): Promise<ServiceResponse<Event>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();
      if (user?.error) {
        throw user.error;
      }

      // Prepare update payload - only include provided fields
      const updatePayload: any = {};

      if (eventData.title !== undefined) updatePayload.title = eventData.title.trim();
      if (eventData.date !== undefined) updatePayload.date = eventData.date;
      if (eventData.time !== undefined) updatePayload.time = eventData.time.trim();
      if (eventData.location !== undefined) updatePayload.location = eventData.location.trim();
      if (eventData.category !== undefined) updatePayload.category = eventData.category.trim();
      if (eventData.description !== undefined) updatePayload.description = eventData.description.trim();
      if (eventData.capacity !== undefined) updatePayload.capacity = Number(eventData.capacity) || 0;
      if (eventData.featured !== undefined) updatePayload.featured = eventData.featured;
      if (eventData.published !== undefined) updatePayload.published = eventData.published;

      const { data, error } = await safeFrom('events')
        .update(updatePayload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('[eventsService.updateEvent] Supabase error:', error, 'ID:', id, 'Payload:', updatePayload);
        throw error;
      }

      const event = data as Event;

      // Audit log
      await auditContent.update('event', id.toString(), {}, {
        title: event.title,
        date: event.date,
        published: event.published,
        updated_at: event.updated_at,
      });

      return successResponse(event);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'eventsService.updateEvent', { id }));
    }
  },

  /**
   * Delete an event
   * @param id - Event ID
   * @returns Success boolean
   */
  async deleteEvent(id: number): Promise<ServiceResponse<boolean>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { error } = await safeFrom('events')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      // Audit log
      await auditContent.delete('event', id.toString());

      return successResponse(true);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'eventsService.deleteEvent', { id }));
    }
  },
};


